/**
 * What to say after an HSN repair run (finding A4, 2026-09-28).
 *
 * The route's own rule, carried to the screen: **a clean run must not read as
 * a finished book.** Restoring every dropped zero can leave thousands of
 * products still needing a code, and a sentence that said only "done" would be
 * the false claim stage 4 spent itself removing. So the residue is always
 * named, and a run that found nothing says that — not that the codes are fine.
 */

import type { HsnRepair } from './api'

function plural(n: number, one: string, many: string): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`
}

export function repairSummary(result: HsnRepair): string {
  const parts: string[] = []
  if (result.codes === 0) {
    parts.push('No dropped zeros to restore.')
  } else {
    parts.push(
      `Restored ${plural(result.codes, 'code', 'codes')} across ` +
        `${plural(result.rows, 'row', 'rows')} in ` +
        `${plural(result.documents, 'document', 'documents')}.`,
    )
  }
  if (result.respected_decisions > 0) {
    parts.push(
      `${plural(result.respected_decisions, 'row', 'rows')} a person had already decided ` +
        `${result.respected_decisions === 1 ? 'was' : 'were'} left as decided.`,
    )
  }
  parts.push(
    result.needs_a_person > 0
      ? `${plural(result.needs_a_person, 'code still needs', 'codes still need')} a person — ` +
          'download the queue for those.'
      : 'Nothing is left for the arithmetic; any code still missing needs the queue.',
  )
  if (result.demoted_periods.length > 0) {
    parts.push(
      `${plural(result.demoted_periods.length, 'period', 'periods')} went back to review, ` +
        'because this changed data already sent.',
    )
  }
  return parts.join(' ')
}
